import React from 'react'
import { Formik } from 'formik'
import { routes } from '@sketch/modules-common'
import { LinkButton, Skeleton, Text } from '@sketch/components'
import { useGetWorkspaceProfileQuery } from '@sketch/gql-types'
import { PROFILE_URL_PREFIX } from '@sketch/constants'
import {
  getValidationSchema,
  WorkspaceProfileURL,
} from '../../../community/containers/WorkspaceProfileURL'
import { PublishProfileSection } from '../../../community/containers/WorkspaceSettingsPublishProfile'
import { useProfileShortURL } from '../../../community/hooks/useProfileShortURL'
import {
  Section,
  Title,
  Description,
  PublishedStatus,
  StyledForm,
  SubmitButton,
  InputWrapper,
  OtherSettingsContainer,
  Strong,
  StyledLink,
} from './WorkspaceSettingsProfile.styles'

interface FormValues {
  shortUrl: string
}

export interface WorkspaceSettingsProfileProps {
  workspaceIdentifier: string
}

interface LoadingGuardProps {
  loading: boolean
  component: React.ReactElement
}

const LoadingGuard: React.FCC<LoadingGuardProps> = ({
  loading,
  component: Component,
}) => {
  if (loading) {
    return <Skeleton width="100%" height="70px" />
  }

  return Component
}

interface LabelProps {
  hasNewUrl: boolean
  currentShortUrl: string
}

const CurrentShortURLLabel: React.FCC<LabelProps> = ({
  hasNewUrl,
  currentShortUrl,
}) => {
  if (hasNewUrl) {
    return (
      <Text textStyle="copy.tertiary.standard.E">
        Your current URL is{' '}
        <StyledLink
          href={routes.WORKSPACE_PROFILE.create({
            shortUrlName: currentShortUrl,
          })}
          external
        >
          {PROFILE_URL_PREFIX}
          {currentShortUrl}
        </StyledLink>
      </Text>
    )
  }

  return (
    <Text textStyle="copy.tertiary.standard.E">
      This is your public profile’s URL.{' '}
      <StyledLink
        href={routes.WORKSPACE_PROFILE.create({
          shortUrlName: currentShortUrl,
        })}
        external
        isUnderlined
      >
        View Profile ↗
      </StyledLink>
    </Text>
  )
}

export const WorkspaceSettingsProfile: React.FCC<
  WorkspaceSettingsProfileProps
> = props => {
  const { workspaceIdentifier } = props

  const { data, loading, error } = useGetWorkspaceProfileQuery({
    variables: { workspaceIdentifier },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  const { identifier, isPublic, shortUrlName } = data?.workspaceProfile ?? {
    identifier: '',
    shortUrlName: '',
  }

  const profileUrlMutationProps = useProfileShortURL(shortUrlName!)
  const {
    updateWorkspaceProfileShortUrl,
    isButtonDisabled,
    isUrlAvailable,
    hasNewUrl,
    isSavingShortUrl,
  } = profileUrlMutationProps

  const handleSubmit = (formData: FormValues) => {
    updateWorkspaceProfileShortUrl({
      variables: {
        input: {
          identifier,
          shortUrlName: formData.shortUrl,
        },
      },
    })
  }

  return (
    <>
      <Section>
        <Title bold>Public Profile</Title>
        <Description>
          Show the world what you do best with a public profile — your space to
          share Documents, Libraries, and Templates with the rest of the design
          community.
        </Description>
      </Section>

      <Section secondary>
        <Title>
          <Strong>Visibility</Strong>:{' '}
          <PublishedStatus>{isPublic ? 'Published' : 'Hidden'}</PublishedStatus>
        </Title>

        <LoadingGuard
          loading={loading}
          component={
            <PublishProfileSection
              error={error}
              isPublic={isPublic}
              profileIdentifier={identifier}
              workspaceIdentifier={workspaceIdentifier}
            />
          }
        />
      </Section>

      <Section secondary>
        <Title>Public Profile URL</Title>
        <LoadingGuard
          loading={loading}
          component={
            <Formik
              initialValues={{ shortUrl: shortUrlName }}
              validationSchema={getValidationSchema('shortUrl')}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {formikBag => {
                const { dirty, values, isValid } = formikBag
                const isEmpty = dirty && values.shortUrl.length === 0

                return (
                  <StyledForm>
                    <WorkspaceProfileURL.Root
                      shortUrl={shortUrlName}
                      name="shortUrl"
                      formikBag={formikBag}
                    >
                      <>
                        <InputWrapper>
                          <WorkspaceProfileURL.Input
                            label={
                              <CurrentShortURLLabel
                                hasNewUrl={hasNewUrl}
                                currentShortUrl={shortUrlName}
                              />
                            }
                            name="shortUrl"
                            formikBag={formikBag}
                            profileShortURLProps={profileUrlMutationProps}
                          />
                          <WorkspaceProfileURL.Error
                            name="shortUrl"
                            formikBag={formikBag}
                            isUrlAvailable={isUrlAvailable}
                          />
                        </InputWrapper>
                        <SubmitButton
                          type="submit"
                          disabled={isButtonDisabled || isEmpty || !isValid}
                          loading={isSavingShortUrl}
                        >
                          Save
                        </SubmitButton>
                      </>
                    </WorkspaceProfileURL.Root>
                  </StyledForm>
                )
              }}
            </Formik>
          }
        />
      </Section>

      <Section secondary>
        <Title>Other Settings</Title>
        <OtherSettingsContainer>
          <Description>
            View your profile’s preview to edit its title, description and
            documents.
            <br />
            Need help setting it up?{' '}
            <StyledLink
              external
              href="https://www.sketch.com/redirect/web/public-profile-docs"
              target="_blank"
              isUnderlined
            >
              Check the documentation ↗
            </StyledLink>
          </Description>
          <LinkButton
            to={{
              pathname: routes.WORKSPACE_PROFILE_EDIT.create({
                workspaceId: workspaceIdentifier,
              }),
            }}
            variant="secondary"
          >
            Edit Profile
          </LinkButton>
        </OtherSettingsContainer>
      </Section>
    </>
  )
}
